import { Equipment } from '../../equipment';
import { Element } from '../element';
import { EquipmentElements } from '../equipmentElements';
import { ExtensionType } from './extension';
import { getMeshBarPlacements } from './mesh';

export enum BarrierType {
	None = 'none',
	Transparent = 'transparent',
	Solid = 'solid',
	Mesh = 'mesh'
}

export enum BarrierSide {
	Front = 'front',
	Back = 'back',
	Left = 'left',
	Right = 'right'
}

export interface BarrierParams {
	barrierType: BarrierType;
	barrierHeight: number;
	barrierColor: string;
}

export function isBarrierNone(params: Pick<BarrierParams, 'barrierType'> | undefined, active?: boolean) {
	return active === false || params?.barrierType === BarrierType.None;
}

export function elementHasVisibleBarrier(element: Element): boolean {
	const extension = element.getExtension<BarrierParams>(ExtensionType.Barrier);

	if (!extension?.active) {
		return false;
	}

	const barrierType = extension.params?.barrierType;

	return Boolean(barrierType && barrierType !== BarrierType.None);
}

export function getSavedBarrierFields(active: boolean, params: BarrierParams) {
	if (isBarrierNone(params, active)) {
		return {
			active: false,
			barrierType: BarrierType.None,
			barrierHeight: params.barrierHeight,
			barrierColor: params.barrierColor
		};
	}

	return {
		active: true,
		barrierType: params.barrierType,
		barrierHeight: params.barrierHeight,
		barrierColor: params.barrierColor
	};
}

export const BARRIER_DISABLED_MESSAGE: string = 'EQUIPMENT_EDIT.CONFIGURATION_PARAMS.BARRIER.DISABLED_HINT' as const;
export const BARRIER_COLOR_DISABLED_MESSAGE: string =
	'EQUIPMENT_EDIT.CONFIGURATION_PARAMS.BARRIER.COLOR_DISABLED_HINT' as const;
export const BARRIER_DEFAULT_HEIGHT = 0.06 as const;
export const BARRIER_DEFAULT_COLOR = '#eaeaea' as const;

export const BARRIER_THICKNESS = 0.01 as const;
export const MESH_STEP_MIN = 0.2 as const;
export const MESH_STEP_MAX = 0.25 as const;

const BASES = [EquipmentElements.BaseRectangle, EquipmentElements.BaseTrapeze] as const;
const BACK_WALLS = [EquipmentElements.BackWall, EquipmentElements.HookWall, EquipmentElements.PerforatedWall] as const;
const SIDE_WALLS = [EquipmentElements.SideWalls] as const;
const SHELVES = [EquipmentElements.Shelf, EquipmentElements.AngleShelf] as const;

function barrierSidesFromWalls(hasBackWall: boolean, hasSideWalls: boolean): Array<BarrierSide> {
	const sides = [BarrierSide.Front];

	if (!hasBackWall) {
		sides.push(BarrierSide.Back);

		if (!hasSideWalls) {
			sides.push(BarrierSide.Left, BarrierSide.Right);
		}
	}

	return sides;
}

export function getFreeSidesForBarrier(element: Element, equipment: Equipment) {
	if (element.parent.is(...SHELVES)) {
		const hasBackWall = element.depend?.parent.is(...BACK_WALLS) ?? false;
		const base = element.findDepend(...BASES);
		const hasSideWalls = base
			? equipment.getRelatedElements(base).some(({ parent }) => parent.is(...SIDE_WALLS))
			: false;

		return barrierSidesFromWalls(hasBackWall, hasSideWalls);
	}

	const relatedElements = equipment.getRelatedElements(element);

	if (!element.parent.is(...BASES) || relatedElements.some(({ parent }) => parent.is(EquipmentElements.Showcase))) {
		return [];
	}

	const hasBackWall = relatedElements.some(({ parent }) => parent.is(...BACK_WALLS));
	const hasSideWalls = relatedElements.some(({ parent }) => parent.is(...SIDE_WALLS));

	return barrierSidesFromWalls(hasBackWall, hasSideWalls);
}

export function getBarrierMeshBarPlacements(length: number): Array<number> {
	return getMeshBarPlacements(length, MESH_STEP_MIN, MESH_STEP_MAX);
}

export function getBarrierMeshBarCenters(length: number): Array<number> {
	const halfBar = BARRIER_THICKNESS / 2;

	return getBarrierMeshBarPlacements(length).map((offset) =>
		Math.min(Math.max(offset, halfBar), Math.max(length - halfBar, halfBar))
	);
}

export interface BarrierPoint {
	x: number;
	z: number;
}

export interface BarrierPolyline {
	points: Array<BarrierPoint>;
	closed: boolean;
}

const ARC_STEP = 0.012;
const MIN_SEGMENT = 1e-8;

interface BarrierPart {
	sides: Array<BarrierSide>;
	points: Array<BarrierPoint>;
}

function pushBarrierLine(parts: Array<BarrierPart>, sides: Array<BarrierSide>, start: BarrierPoint, end: BarrierPoint) {
	if (Math.hypot(end.x - start.x, end.z - start.z) > MIN_SEGMENT) {
		parts.push({ sides, points: [start, end] });
	}
}

function stitchBarrierParts(parts: Array<BarrierPart>, indices: Array<number>, closed: boolean): BarrierPolyline {
	const points: Array<BarrierPoint> = [];

	indices.forEach((index, order) => {
		const partPoints = parts[index].points;

		if (order === 0) {
			points.push(...partPoints);
		} else {
			points.push(...partPoints.slice(1));
		}
	});

	if (closed && points.length > 1) {
		const first = points[0];
		const last = points[points.length - 1];

		if (Math.hypot(first.x - last.x, first.z - last.z) < MIN_SEGMENT) {
			points.pop();
		}
	}

	return { points, closed };
}

function sampleArc(cx: number, cz: number, radius: number, startAngle: number, endAngle: number): Array<BarrierPoint> {
	const sweep = endAngle - startAngle;
	const count = Math.max(1, Math.ceil((Math.abs(sweep) * radius) / ARC_STEP));

	return Array.from({ length: count + 1 }, (_, index) => {
		const angle = startAngle + (sweep * index) / count;

		return { x: cx + Math.cos(angle) * radius, z: cz + Math.sin(angle) * radius };
	});
}

export function getRoundedBarrierPolylines(
	width: number,
	depth: number,
	leftRadius: number,
	rightRadius: number,
	sides: Array<BarrierSide>,
	offset: number = BARRIER_THICKNESS / 2
): Array<BarrierPolyline> {
	if (!sides.length || width <= 0 || depth <= 0) {
		return [];
	}

	const halfW = width / 2;
	const halfD = depth / 2;
	const leftR = Math.max(0, Math.min(leftRadius, halfW, halfD));
	const rightR = Math.max(0, Math.min(rightRadius, halfW, halfD));
	const outerLeftR = leftR > 0 ? leftR + offset : 0;
	const outerRightR = rightR > 0 ? rightR + offset : 0;
	const frontZ = halfD + offset;
	const backZ = -halfD - offset;
	const rightX = halfW + offset;
	const leftX = -halfW - offset;
	const frontLeftX = leftR > 0 ? -halfW + leftR : leftX;
	const frontRightX = rightR > 0 ? halfW - rightR : rightX;
	const rightFrontZ = rightR > 0 ? halfD - rightR : frontZ;
	const rightBackZ = rightR > 0 ? -halfD + rightR : backZ;
	const leftBackZ = leftR > 0 ? -halfD + leftR : backZ;
	const leftFrontZ = leftR > 0 ? halfD - leftR : frontZ;

	const parts: Array<BarrierPart> = [];

	pushBarrierLine(parts, [BarrierSide.Front], { x: frontLeftX, z: frontZ }, { x: frontRightX, z: frontZ });

	if (outerRightR > 0) {
		parts.push({
			sides: [BarrierSide.Front, BarrierSide.Right],
			points: sampleArc(halfW - rightR, halfD - rightR, outerRightR, Math.PI / 2, 0)
		});
	}

	pushBarrierLine(parts, [BarrierSide.Right], { x: rightX, z: rightFrontZ }, { x: rightX, z: rightBackZ });

	if (outerRightR > 0) {
		parts.push({
			sides: [BarrierSide.Right, BarrierSide.Back],
			points: sampleArc(halfW - rightR, -halfD + rightR, outerRightR, 0, -Math.PI / 2)
		});
	}

	pushBarrierLine(parts, [BarrierSide.Back], { x: frontRightX, z: backZ }, { x: frontLeftX, z: backZ });

	if (outerLeftR > 0) {
		parts.push({
			sides: [BarrierSide.Back, BarrierSide.Left],
			points: sampleArc(-halfW + leftR, -halfD + leftR, outerLeftR, -Math.PI / 2, -Math.PI)
		});
	}

	pushBarrierLine(parts, [BarrierSide.Left], { x: leftX, z: leftBackZ }, { x: leftX, z: leftFrontZ });

	if (outerLeftR > 0) {
		parts.push({
			sides: [BarrierSide.Left, BarrierSide.Front],
			points: sampleArc(-halfW + leftR, halfD - leftR, outerLeftR, Math.PI, Math.PI / 2)
		});
	}

	const enabled = parts.map((part) => part.sides.some((side) => sides.includes(side)));
	const count = parts.length;

	if (!count || !enabled.some(Boolean)) {
		return [];
	}

	if (count === 1) {
		return enabled[0] ? [stitchBarrierParts(parts, [0], false)] : [];
	}

	if (enabled.every(Boolean)) {
		return [
			stitchBarrierParts(
				parts,
				parts.map((_, index) => index),
				true
			)
		];
	}

	const polylines: Array<BarrierPolyline> = [];

	for (let index = 0; index < count; index++) {
		const previous = (index - 1 + count) % count;

		if (!enabled[index] || enabled[previous]) {
			continue;
		}

		const run: Array<number> = [];

		for (let step = 0; step < count && enabled[(index + step) % count]; step++) {
			run.push((index + step) % count);
		}

		polylines.push(stitchBarrierParts(parts, run, false));
	}

	return polylines;
}

export function getPolylineLength(points: Array<BarrierPoint>, closed: boolean): number {
	const count = closed ? points.length : points.length - 1;
	let length = 0;

	for (let index = 0; index < count; index++) {
		const start = points[index];
		const end = points[(index + 1) % points.length];

		length += Math.hypot(end.x - start.x, end.z - start.z);
	}

	return length;
}

export function getPointAlongPolyline(
	points: Array<BarrierPoint>,
	closed: boolean,
	distance: number
): BarrierPoint | null {
	if (!points.length) {
		return null;
	}

	const total = getPolylineLength(points, closed);

	if (total <= 0) {
		return points[0];
	}

	if (!closed) {
		if (distance <= 0) {
			return points[0];
		}

		if (distance >= total) {
			return points[points.length - 1];
		}

		let remaining = distance;

		for (let index = 0; index < points.length - 1; index++) {
			const start = points[index];
			const end = points[index + 1];
			const segment = Math.hypot(end.x - start.x, end.z - start.z);

			if (remaining <= segment || index === points.length - 2) {
				const t = segment > 0 ? remaining / segment : 0;

				return { x: start.x + (end.x - start.x) * t, z: start.z + (end.z - start.z) * t };
			}

			remaining -= segment;
		}

		return points[points.length - 1];
	}

	let remaining = ((distance % total) + total) % total;

	for (let index = 0; index < points.length; index++) {
		const start = points[index];
		const end = points[(index + 1) % points.length];
		const segment = Math.hypot(end.x - start.x, end.z - start.z);

		if (remaining <= segment || index === points.length - 1) {
			const t = segment > 0 ? remaining / segment : 0;

			return { x: start.x + (end.x - start.x) * t, z: start.z + (end.z - start.z) * t };
		}

		remaining -= segment;
	}

	return points[0];
}
